import React, { useContext, useMemo, useRef, useState } from "react";

import API from "../utils/api";
import { getSequentialTableDefaults } from "./helpers";
import moment from "moment";
import { useSnackbar } from "./snackbar.context";
import { useUser } from "./user.context";
import { PermissionProvider } from "./permission.context";
import { STUDY_PLAN_TYPES, UPDATE_COUNTRIES_TRACEABILITY } from "./constants/study.context.constants";
import { buildTransformedObject } from "../utils/transformStudyPlan";
import { buildNewPlanDefaults } from "../utils/createNewPlanDefaults";
import { calculateSiteActivationStats, getDefaultStats } from "../utils/studyPlanUtils";
import { updateCalculatedFields } from "../../study.plans/study.plan/countries.and.site.groups/sequential.cohort.setup.table/utilities";
import { resolveSavePlanSnackbar, stripApiMessageFields } from "../utils/apiResponseMessages";
import { resolveSiteIdEffort } from "../utils/siteIdEffort";

const StudyContext = React.createContext();

export function useStudyPlan() {
  return useContext(StudyContext);
}

export function StudyProvider({ children }) {
  const [studyPlan, setStudyPlan] = useState(null);
  const [seqCohorts, setSeqCohorts] = useState([]);
  const [seqCohortColumnHeaders, setSeqCohortColumnHeaders] = useState({
    columnHeader3: "DLT",
    columnHeader4: "SRC",
  });
  const HEADER_DEFAULTS = {
    columnHeader3: "DLT",
    columnHeader4: "SRC",
  };
  const seqCohortColumnHeadersRef = useRef({
    columnHeader3: "DLT",
    columnHeader4: "SRC",
  });
  const seqCohortsRef = useRef(null);
  const [seqCohortChangeLog, setSeqCohortChangeLog] = useState({});
  const [seqCohortsStatus, setSeqCohortsStatus] = useState("idle");
  const [study, setStudy] = useState(null);
  const [changelog, setChangelog] = useState({});
  const [countryChangelog, setCountryChangelog] = useState({});
  const [countryMilestoneChangelog, setCountryMilestoneChangelog] = useState(
    {},
  );
  const [simulationResults, setSimulationResults] = useState({});
  const [studyActualsData, setStudyActualsData] = useState({});
  const [studyRemodelData, setStudyRemodelData] = useState({});

  const [loadingStudy, setLoadingStudy] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [actualsLoading, setActualsLoading] = useState(false);
  const [remodelLoading, setRemodelLoading] = useState(false);
  const [remodelUpdateLoading, setRemodelUpdateLoading] = useState(false);
  const [lActualsLoading, setLActualsLoading] = useState(false);
  const [deleteCountryLoading, setDeleteCountryLoading] = useState(false);
  const [isCountryUpdate, setIsCountryUpdate] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [activationProfilesChanged, setActivationProfilesChanged] = useState(UPDATE_COUNTRIES_TRACEABILITY.NONE);
  const [isEditingAPTableEnabled, setIsEditingAPTableEnabled] = useState(false);

  const [isSequential, hideEnrollmentDuration] = useMemo(() => {
    const { studyPlanType, cohortType } = studyPlan || {};
    return [cohortType === 2, studyPlanType === 1 || cohortType === 2];
  }, [studyPlan]);

  const { showSnackbar } = useSnackbar();
  const { isReadOnlyUser } = useUser();
  const canEditPlan = studyPlan?.scenarioStatus === "Draft" && !isReadOnlyUser;

  const siteActivationStats = studyPlan
    ? calculateSiteActivationStats(studyPlan)
    : getDefaultStats(studyPlan?.studyPlanType) || {};

  function deleteStudy(studyId, studyPlanId) {
    return API.deleteStudyPlan(studyId, studyPlanId);
  }

  function fetchStudy(id) {
    setLoadingStudy(true);
    return API.getStudy(id).then((res) => {
      setStudy(res.data[0]);
      setLoadingStudy(false);
    });
  }

  function fetchCohortStudyPlan(id) {
    setSeqCohortsStatus("loading");
    return API.getCohortStudyPlan(id)
      .then((res) => {
        const { studyPlanId } = studyPlan;
        
        if (res.data.length > 0) {
          // 1. Data exists: Use DB values or Fallback to defaults
          const headers = {
            columnHeader3: res.data[0].columnHeader3 || HEADER_DEFAULTS.columnHeader3,
            columnHeader4: res.data[0].columnHeader4 || HEADER_DEFAULTS.columnHeader4,
          };
          
          // Migrate old hardcoded values to match current headers
          const migratedData = res.data.map((cohort) => {
            let predecessorActivity = cohort.predecessorActivity;
            
            // Replace old hardcoded values with current header values
            if (predecessorActivity === HEADER_DEFAULTS.columnHeader3 || predecessorActivity === "DTL") {
              predecessorActivity = headers.columnHeader3;
            } else if (predecessorActivity === HEADER_DEFAULTS.columnHeader4) {
              predecessorActivity = headers.columnHeader4;
            }
            
            return {
              ...cohort,
              predecessorActivity,
            };
          });
          
          setSeqCohortColumnHeaders(headers);
          seqCohortColumnHeadersRef.current = headers;
          setSeqCohorts(migratedData);
          seqCohortsRef.current = migratedData;
        } else {
          // 2. Data DOES NOT exist: Force reset to defaults for this new plan
          setSeqCohortColumnHeaders(HEADER_DEFAULTS);
          seqCohortColumnHeadersRef.current = HEADER_DEFAULTS;
          setSeqCohorts([getSequentialTableDefaults(studyPlanId)]);
          seqCohortsRef.current = null; 
        }
        setSeqCohortsStatus("resolved");
      })
      .catch((err) => {
        setSeqCohortsStatus("error");
      });
  }

  function updateCohortStudyPlan(id, noLoading = false, updatedValues = null) {
    if (!noLoading) {
      setSeqCohortsStatus("loading");
    }
    const localSeqCohorts = (updatedValues || seqCohorts).map((sc) => {
      const lsc = { ...sc };
      delete lsc.key;
      const keys = Object.keys(lsc);
      keys.forEach((key) => {
        if (
          [
            "cohortStartDate",
            "dtlEndDate",
            "enrollingEndDate",
            "screeningEndDate",
            "srcEndDate",
          ].includes(key)
        ) {
          if (lsc[key])
            lsc[key] = moment.utc(lsc[key], "D-MMM-YY").format("YYYY-MM-DD");
        }
      });
      return { ...lsc, ...seqCohortColumnHeaders };
    });
    return API.updateCohortStudyPlan(id, localSeqCohorts)
      .then(async (res) => {
        if (!noLoading) {
          const { studyPlanId } = studyPlan;
          await fetchCohortStudyPlan(studyPlanId);
          setSaveLoading(false);
        } else {
          seqCohortColumnHeadersRef.current = { ...seqCohortColumnHeaders };
          seqCohortsRef.current = [...seqCohorts];
        }
      })
      .catch((err) => {
        setSeqCohortsStatus("error");
        if (!noLoading) setSaveLoading(false);
      });
  }

  function validateSequentialCohort(pathname) {
    if (
      Object.keys(seqCohortChangeLog).length === 0 ||
      !(
        pathname.includes("countries-and-site-groups") ||
        pathname.includes("review")
      )
    )
      return true;

    let valid = true;
    seqCohorts.forEach((sc, index) => {
      let keys = Object.keys(sc);
      keys.forEach((key) => {
        if (index === 0) {
          if (!["predecessorCohortName", "predecessorActivity"].includes(key)) {
            if (!sc[key] && sc[key] !== 0) valid = false;
          }
        } else {
          if (!sc[key] && sc[key] !== 0) valid = false;
        }
      });
    });

    return valid;
  }

  function fetchStudyPlan(id) {
    setLoadingPlan(true);
    setSeqCohortsStatus("idle");
    return API.getStudyPlan(id).then((res) => {
      const plan = stripApiMessageFields(res.data[0]);
      setStudyPlan(plan);
      setLoadingPlan(false);
      return plan;
    });
  }

  function fetchStudyPlanSilent(id) {
    return API.getStudyPlan(id).then((res) => {
      const plan = res.data[0];
      setStudyPlan(stripApiMessageFields(plan));
      return { plan, response: res };
    });
  }

  function showPlanSaveSnackbar(
    defaultMessage,
    beforePlan,
    afterPlan,
    ...sources
  ) {
    const { message, severity } = resolveSavePlanSnackbar(
      defaultMessage,
      beforePlan,
      afterPlan,
      ...sources,
    );
    showSnackbar(message, severity);
  }

  
  function updatePlan(property, value) {
    const { studyPlanId } = studyPlan;
    const newStudyPlan = { ...studyPlan, [property]: value };
    setChangelog((prev) => ({ studyPlanId, ...prev, [property]: value }));
    setStudyPlan(newStudyPlan);
  }

  function getRecalculatedCohorts(currentStudyPlan, currentSeqCohorts) {
    const croStartDate = currentStudyPlan.croProjectStartDateMillis
      ? moment.utc(currentStudyPlan.croProjectStartDateMillis).format("D/MMM/YY")
      : "";

    let fpsStartDate = "";
    const { countries } = currentStudyPlan;
    if (countries) {
      const fpsDateValues = countries.map((country) => {
        const { milestones } = country.siteGroups[0];
        let dateValue = "";
        milestones.forEach((ml) => {
          if (ml["milestoneName"] === "DURATION_TO_FPS")
            dateValue = ml["milestoneDateMillis"];
        });
        return dateValue;
      });
      fpsStartDate = moment.utc(Math.min(...fpsDateValues)).format("D/MMM/YY");
    }

    if (!fpsStartDate || !croStartDate) return currentSeqCohorts;
    return updateCalculatedFields(currentSeqCohorts, fpsStartDate, croStartDate);
  }

  async function savePlan({
    isNew = false,
    redirect = undefined,
    onClose = undefined,
  }) {
    const { studyPlanId, studyId } = studyPlan;
    setSaveLoading(true);

    if (isNew) {
      try {
        const res = await API.createNewPlan(studyPlan);
        setChangelog({});
        redirect && redirect(res.data[0].Id);
        setSaveLoading(false);
        return true;
      } catch (err) {
        setSaveLoading(false);
        showSnackbar("Failed to create new plan.", "error");
        return false;
      }
    } else {
      Object.keys(studyPlan).forEach((key) => {
        if (!changelog.hasOwnProperty(key)) {
          changelog[key] = studyPlan[key];
        }
      });

      // Keep saved/manual Site-ID Effort. Only fill when empty (do not overwrite).
      changelog.countries?.forEach((country) => {
        const sg = country?.siteGroups?.[0];
        if (!sg) return;
        if (sg.timeSiteIdSize) return;

        const lowerBound = sg.parameters?.[0]?.lowerBound;
        const upperBound = sg.parameters?.[0]?.upperBound;
        const size = resolveSiteIdEffort(null, lowerBound, upperBound);
        if (size) {
          sg.timeSiteIdSize = size;
        }
      });

      if (studyPlan?.ActualStep === 1) {
        const seqChange = Object.keys(seqCohortChangeLog).length;
        const changeLogKeys = Object.keys(changelog);
        const countryUpdate = changeLogKeys.length === 2 && !!changelog["countries"];
        
        if (!countryUpdate && Object.keys(changelog).length) {
          try {
            const beforePlan = JSON.parse(JSON.stringify(studyPlan));
            const res = await API.updatePlan(changelog, studyId);
            setChangelog({});
            setCountryChangelog({});
            setCountryMilestoneChangelog({});

            let freshPlan1;
            let fetchRes1;
            try {
              ({ plan: freshPlan1, response: fetchRes1 } =
                await fetchStudyPlanSilent(studyPlanId));
            } catch (fetchErr) {
              console.error("Failed to refresh plan after save.", fetchErr);
            }

            showPlanSaveSnackbar(
              "Successfully saved plan.",
              beforePlan,
              freshPlan1,
              res,
              fetchRes1,
            );

            if (isSequential && freshPlan1) {
              const recalculated = getRecalculatedCohorts(freshPlan1, seqCohorts);
              setSeqCohorts(recalculated);
              await updateCohortStudyPlan(studyId, true, recalculated);
              await fetchCohortStudyPlan(studyPlanId);
              // /SequentialCohort recalculates country milestones server-side; refetch the plan to pick them up.
              try {
                await fetchStudyPlanSilent(studyPlanId);
              } catch (fetchErr) {
                console.error("Failed to refresh plan after sequential cohort save.", fetchErr);
              }
            }

            setSaveLoading(false);

            if (redirect) {
              redirect();
            }
            onClose && onClose();
            return true;
          } catch (err) {
            setSaveLoading(false);
            showSnackbar("Failed to save plan.", "error");
            return false;
          }
        }
        setSaveLoading(false);
        return true;
      } else if (studyPlan?.ActualStep === 2 || studyPlan?.ActualStep === 3) {
        const transformedObject = buildTransformedObject(changelog, studyId);
        const beforePlan = JSON.parse(JSON.stringify(studyPlan));
        try {
          const res = await API.updatePlanStep2(transformedObject);
          const payload = res?.data?.[0] ?? {};
          const returnCode = Number(payload.returnCode ?? 200);
          const isSuccess = Number(res.status) === 200 && returnCode === 200;

          if (isSuccess) {
            setChangelog({});
            setSeqCohortChangeLog({});
            setCountryChangelog({});
            setCountryMilestoneChangelog({});

            let freshPlan2;
            let fetchRes2;
            try {
              ({ plan: freshPlan2, response: fetchRes2 } =
                await fetchStudyPlanSilent(studyPlanId));
            } catch (fetchErr) {
              console.error("Failed to refresh plan after save.", fetchErr);
            }

            showPlanSaveSnackbar(
              "Successfully saved plan.",
              beforePlan,
              freshPlan2,
              res,
              fetchRes2,
            );

            if (isSequential && freshPlan2) {
              const recalculated = getRecalculatedCohorts(freshPlan2, seqCohorts);
              setSeqCohorts(recalculated);
              await updateCohortStudyPlan(studyId, true, recalculated);
              await fetchCohortStudyPlan(studyPlanId);
              // /SequentialCohort recalculates country milestones server-side; refetch the plan to pick them up.
              try {
                await fetchStudyPlanSilent(studyPlanId);
              } catch (fetchErr) {
                console.error("Failed to refresh plan after sequential cohort save.", fetchErr);
              }
            }

            setSaveLoading(false);

            if (redirect) {
              redirect();
            }
            onClose && onClose();
            return true;
          } else {
            const errorMessage =
              payload.returnDesc ||
              (Number.isFinite(returnCode)
                ? `Failed to save plan (${returnCode})`
                : "Failed to save plan.");
            showSnackbar(errorMessage, "error");
            setSaveLoading(false);
            return false;
          }
        } catch (err) {
          setSaveLoading(false);
          console.error("API Error:", err);
          showSnackbar("Failed to save plan.", "error");
          return false;
        }
      } else {
        setSaveLoading(false);
        showSnackbar("Unknown step configuration.", "error");
        return false;
      }
    }
  }

  function updateStudyPlan(updates) {
    const { studyPlanId, studyId } = studyPlan;
    const body = {
      studyPlanId,
      studyId,
      ...updates,
    };
    API.updateStudyPlan(body, studyId).then((res) => {
      setStudyPlan({ ...studyPlan, ...updates });
    });
  }

  function quickSave(updates) {
    const { studyPlanId } = studyPlan;
    const body = {
      studyPlanId,
      ...updates,
    };
    API.updatePlan(body).then((res) => {
      setStudyPlan({ ...studyPlan, ...updates });
    });
  }

  function updateCountries(
    countryId,
    siteGroupId,
    update,
    isDelete = false,
    isActivationProfilesChange = UPDATE_COUNTRIES_TRACEABILITY.NONE
  ) {
    const { studyPlanId } = studyPlan;
    if (!isDelete) {
      setCountryChangelog((prev) => ({
        ...prev,
        [countryId]: {
          studyPlanId,
          countryId,
          siteGroupId,
          ...prev[countryId],
          ...update,
        },
      }));
    }

    if (isActivationProfilesChange === UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES) {
      setActivationProfilesChanged(UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES);
    }

    _countryStateUpdate(countryId, update);
  }

  function updateCountryMilestones(
    countryId,
    siteGroupId,
    update,
    changelog_update,
    isDelete = false,
  ) {
    const { studyPlanId } = studyPlan;
    if (!isDelete) {
      setCountryMilestoneChangelog((prev) => ({
        ...prev,
        [countryId]: {
          studyPlanId,
          countryId,
          siteGroupId,
          ...prev[countryId],
          milestones: [changelog_update[0]],
        },
      }));
    }

    _countryStateUpdate(countryId, update);
  }

  function addCountry(new_country) {
    const { countries } = studyPlan;
    countries.push(new_country);
    setStudyPlan({ ...studyPlan, countries });
  }

  async function _countryStateUpdate(countryId, update) {
    const countryIndex = studyPlan.countries
      .map((country) => country.countryId)
      .indexOf(countryId);
    const country = studyPlan.countries[countryIndex];
    const updated_site_group = { ...country.siteGroups[0], ...update };
    const new_country = { ...country };
    new_country.siteGroups[0] = updated_site_group;
    const new_countries = [...studyPlan.countries];
    new_countries[countryIndex] = new_country;

    updatePlan("countries", new_countries);
  }

  async function saveCountries(redirect = undefined, onClose = undefined) {
    const { studyPlanId } = studyPlan;
    const beforePlan = JSON.parse(JSON.stringify(studyPlan));

    let keys = Object.keys(countryChangelog);
    let res = {};
    try {
      for (let i = 0; i < keys.length; i++) {
        const body = {
          studyPlanId,
          countryId: keys[i],
          siteGroups: [countryChangelog[keys[i]]],
        };
        res = await API.updateCountry(body);
      }

      const afterPlan = res.data?.[0] ?? res.data;
      showPlanSaveSnackbar("Save was successful!", beforePlan, afterPlan, res);
      setCountryChangelog({});
      setIsCountryUpdate(false);
      redirect && redirect();
      onClose && onClose();
      setStudyPlan(stripApiMessageFields(afterPlan));
      setSaveLoading(false);
    } catch (err) {
      showSnackbar("Not all saves were successful.", "error");
      setSaveLoading(false);
    }
  }

  const createNewCohortPlan = (
    selectedStudyPlans,
    studyId,
    values,
    redirect,
  ) => {
    if (selectedStudyPlans.length < 2) return;
    const combinedPlans = selectedStudyPlans.map((ssp) => ({
      studyId: ssp?.studyId,
      studyPlanId: ssp?.studyPlanId,
      studyPlanName: values?.studyPlanName,
      studyPlanDescr: values?.studyPlanDescr,
    }));
    setSaveLoading(true);
    API.createNewCohortPlan(combinedPlans, studyId)
      .then((res) => {
        setSaveLoading(false);
        setChangelog({});
        if (res.data[0].returnCode === "200") {
          showSnackbar("Study plan created successfully.", "success");
          setStudyPlan({
            studyPlanId: res.data[0].studyPlanId,
            studyId: studyId,
          });
          redirect && redirect(res.data[0].studyPlanId);
        } else {
          showSnackbar(
            "Unable to create study plan. Please try again.",
            "error",
          );
        }
      })
      .catch((err) => {
        showSnackbar("Unable to create study plan. Please try again.", "error");
      });
  };

  const createNewPlan = async (scenario, sequentialCohort = "false") => {
    clearStudyPlan();
    setLoadingPlan(true);
    const isSequentialCohort = JSON.parse(sequentialCohort);
    const newPlan = buildNewPlanDefaults(study, scenario, isSequentialCohort);
    setStudyPlan(newPlan);
    setLoadingPlan(false);
  };

  const removeCountry = (body, studyId, countryId, cb) => {
    setDeleteCountryLoading(true);
    API.deleteCountry(body, studyId)
      .then((res) => {
        cb();

        setDeleteCountryLoading(false);

        const newStudyPlan = { ...studyPlan };

        newStudyPlan.countries = newStudyPlan.countries.filter(
          (item) => item.countryId !== countryId,
        );

        setStudyPlan(newStudyPlan);

        showSnackbar("Successfully removed country.", "success");
      })
      .catch((err) => {
        setDeleteCountryLoading(false);
        showSnackbar("Failed to remove country.", "error");
      });
  };

  const clearChangeLog = () => {
    setChangelog({});
    setCountryChangelog({});
    setSeqCohortChangeLog({});
    setCountryMilestoneChangelog({});

    // Fix: Reset to hardcoded defaults, NOT the potentially stale ref
    setSeqCohortColumnHeaders(HEADER_DEFAULTS);
    seqCohortColumnHeadersRef.current = HEADER_DEFAULTS;

    if (seqCohortsRef.current) {
      setSeqCohorts(seqCohortsRef.current);
    } else {
      // If we're clearing a brand new plan that hasn't been saved yet
      setSeqCohorts([getSequentialTableDefaults(studyPlan?.studyPlanId)]);
    }
  };
  const clearStudyPlan = () => {
    setStudyPlan(null);
    clearChangeLog();
  };

  const resetStudyPlanWithCountry = (studyPlan, deletedCountryId) => {
    const { countries } = studyPlan;
    const lCountries = [];
    let lCountryChangeLog = countryChangelog;
    if (deletedCountryId) {
      lCountryChangeLog = {};
      Object.keys(countryChangelog).map((key) => {
        if (key !== deletedCountryId) {
          lCountryChangeLog[key] = countryChangelog[key];
        }
        return "";
      });
    }
    for (let i = 0; i < countries.length; i++) {
      let c = countries[i];
      if (lCountryChangeLog[c.countryId]) {
        lCountries.push({
          ...c,
          siteGroups: [
            { ...c["siteGroups"][0], ...lCountryChangeLog[c.countryId] },
          ],
        });
      } else {
        lCountries.push(c);
      }
    }
    setStudyPlan({ ...studyPlan, countries: lCountries });
    if (deletedCountryId) setCountryChangelog(lCountryChangeLog);
  };

  const resetStudyPlan = (studyPlan) => {
    setStudyPlan(studyPlan);
    clearChangeLog();
  };

  const fetchMilestoneDates = (body) => API.getMilestoneDates(body);

  const fetchAllCountries = async () => {
    if (allCountries.length === 0) {
      API.getCountries().then((res) => {
        setAllCountries((prev) => [...prev, ...res.data]);
      });
    }
  };

  const runSimulation = (id = undefined) => {
    setSimulationResults({});
    if (id) {
      API.runSimulation({ studyPlanId: id }).then((res) => {});
    } else {
      API.runSimulation({ studyPlanId: studyPlan.studyPlanId }).then(
        (res) => {},
      );
    }
  };

  const checkSimulationStatus = async () => {
    return await API.checkSimulationStatus(studyPlan.studyPlanId).then(
      (res) => res.data,
    );
  };
  const fetchSimulationResults = async () =>
    API.getSimulationResults(studyPlan.studyPlanId).then((res) =>
      setSimulationResults((prev) => ({ ...prev, ...res.data })),
    );

  const fetchSimulationDetailsByMonth = async (studyType = "results") =>
    API.getSimulationDetailTableByMonth(studyPlan.studyPlanId, studyType)
      .then((res) => {
        if (studyType === "results") {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByMonth: res.data,
          }));
        } else {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByMonthCohort: res.data,
          }));
        }
      })
      .catch((error) => console.log(error));

  const fetchSimulationDetailsByMonthNonCumulative = async (
    studyType = "results",
  ) =>
    API.getSimulationDetailTableByMonthNonCumulative(
      studyPlan.studyPlanId,
      studyType,
    )
      .then((res) => {
        if (studyType === "results") {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByMonthNonCumulative: res.data,
          }));
        } else {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByMonthNonCumulativeCohort: res.data,
          }));
        }
      })
      .catch((error) => console.log(error));

  const fetchSimulationDetailsByWeekNonCumulative = async (
    studyType = "results",
  ) =>
    API.getSimulationDetailTableByWeekNonCumulative(
      studyPlan.studyPlanId,
      studyType,
    )
      .then((res) => {
        if (studyType === "results") {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByWeekNonCumulative: res.data,
          }));
        } else {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByWeekNonCumulativeCohort: res.data,
          }));
        }
      })
      .catch((error) => console.log(error));

  const fetchSimulationDetailsByWeek = async (studyType = "results") =>
    API.getSimulationDetailTableByWeek(studyPlan.studyPlanId, studyType)
      .then((res) => {
        if (studyType === "results") {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByWeek: res.data,
          }));
        } else {
          setSimulationResults((prev) => ({
            ...prev,
            detailsByWeekCohort: res.data,
          }));
        }
      })
      .catch((error) => console.log(error));

  const fetchSimulationEnrollmentTable = async () =>
    API.getSimulationEnrollmentTable(studyPlan.studyPlanId)
      .then((res) =>
        setSimulationResults((prev) => ({
          ...prev,
          enrollmentTable: res.data,
        })),
      )
      .catch((error) => console.log(error));

  const fetchSimulationStartupTable = async () =>
    API.getSimulationStartupTable(studyPlan.studyPlanId)
      .then((res) =>
        setSimulationResults((prev) => ({
          ...prev,
          startupTable: res.data,
        })),
      )
      .catch((error) => console.log(error));

  const fetchSimulationSummaryTable = async (studyType = "results") =>
    API.getSimulationSummaryTable(studyPlan.studyPlanId, studyType)
      .then((res) => {
        if (studyType === "results") {
          setSimulationResults((prev) => ({ ...prev, summaryTable: res.data }));
        } else {
          setSimulationResults((prev) => ({
            ...prev,
            summaryTableCohort: res.data,
          }));
        }
      })
      .catch((error) => console.log(error));

  const fetchSimulationDurationTable = async () =>
    API.getSimulationDurationTable(studyPlan.studyPlanId)
      .then((res) =>
        setSimulationResults((prev) => ({
          ...prev,
          durationTable: res.data,
        })),
      )
      .catch((error) => console.log(error));

  const fetchSimulationTimelineTable = async () =>
    API.getSimulationTimelineTable(studyPlan.studyPlanId)
      .then((res) =>
        setSimulationResults((prev) => ({
          ...prev,
          timelineTable: res.data,
        })),
      )
      .catch((error) => console.log(error));

  const fetchOverallChart = async () =>
    API.getOverallChart(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            overallChart: res.data.Study?.table_body.Group,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });

  const clearSimData = () => setSimulationResults({});

  const fetchCountryChart = async () =>
    API.getCountryChart(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            countryChart: res.data.Study?.table_body.Group,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });

  const fetchRegionChart = async () => {
    API.getRegionChart(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            regionChartMonthly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
    API.getRegionChart(studyPlan.studyPlanId, "WEEK")
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            regionChartWeekly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchSiteGroupChart = async () => {
    API.getSiteGroupChart(studyPlan.studyPlanId, "MONTH")
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            siteGroupChartMonthly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
    API.getSiteGroupChart(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            siteGroupChartWeekly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchCohortChartMonth = async () => {
    API.getCohortChartMonthly(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            cohortChartMonthly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchCohortChartWeek = async () => {
    API.getCohortChartWeekly(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            cohortChartWeekly: res.data.Study?.table_body,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchCohortTimelineChart = async () => {
    API.getCohortTimelineChart(studyPlan.studyPlanId)
      .then((res) => {
        setSimulationResults((prevState) => {
          return {
            ...prevState,
            cohortTimelineChart: res.data,
          };
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchActualsTable = async () => {
    setActualsLoading(true);
    API.getStudyActuals(study.studyId)
      .then((res) => {
        setActualsLoading(false);
        setStudyActualsData((prevState) => {
          return {
            ...prevState,
            actualsData: res.data,
          };
        });
      })
      .catch((err) => {
        setActualsLoading(false);
        console.error(err);
      });
  };

  const fetchStudyRemodel = async () => {
    setRemodelLoading(true);
    API.getStudyRemodel(study.studyId)
      .then((res) => {
        setStudyRemodelData((prevState) => {
          return {
            ...prevState,
            ...(res.data[0] || {}),
          };
        });
        setRemodelLoading(false);
      })
      .catch((err) => {
        setRemodelLoading(false);
        console.error(err);
      });
  };

  const updateStudyRemodel = async () => {
    setRemodelUpdateLoading(true);
    return API.updateStudyRemodel(study.studyId, studyRemodelData)
      .then((res) => {
        setRemodelUpdateLoading(false);
        return res;
      })
      .catch((err) => {
        setRemodelUpdateLoading(false);
        throw err;
      });
  };

  const loadStudyActuals = async () => {
    setLActualsLoading(true);
    API.loadStudyActuals(study.studyId)
      .then((res) => {
        setLActualsLoading(false);
        setStudyActualsData((prevState) => {
          return {
            ...prevState,
            loadActualsData: res.data,
            actualsData: res.data,
          };
        });
      })
      .catch((err) => {
        setLActualsLoading(false);
        console.error(err);
      });
  };

  let siteGroupId = 0;
  if (
    studyPlan &&
    studyPlan.countries &&
    studyPlan.countries[0] &&
    studyPlan.countries[0].siteGroups &&
    studyPlan.countries[0].siteGroups[0] &&
    studyPlan.countries[0].siteGroups[0].siteGroupId
  ) {
    siteGroupId = studyPlan.countries[0].siteGroups[0].siteGroupId;
  }
  const value = {
    deleteStudy,
    study,
    studyPlan,
    loadingStudy,
    loadingPlan,
    unsavedChange:
      Object.keys({
        ...changelog,
        ...countryChangelog,
        // ...saChangeLog[siteGroupId],
      }).length > 0 ||
      (studyPlan?.studyPlanType === 2 &&
        studyPlan?.studyPlanTypeDetail === "Cohort (D)" &&
        Object.keys(seqCohortChangeLog).length > 0),
    fetchStudy,
    fetchStudyPlan,
    fetchCohortStudyPlan,
    updatePlan,
    savePlan,
    saveLoading,
    actualsLoading,
    lActualsLoading,
    quickSave,
    resetStudyPlan,
    resetStudyPlanWithCountry,
    updateCountries,
    updateCountryMilestones,
    saveCountries,
    addCountry,
    createNewPlan,
    removeCountry,
    deleteCountryLoading,
    clearChangeLog,
    clearStudyPlan,
    fetchMilestoneDates,
    runSimulation,
    checkSimulationStatus,
    simulationResults,
    fetchSimulationResults,
    fetchSimulationDetailsByMonth,
    fetchSimulationDetailsByMonthNonCumulative,
    fetchSimulationDetailsByWeek,
    fetchSimulationDetailsByWeekNonCumulative,
    fetchSimulationEnrollmentTable,
    fetchSimulationStartupTable,
    fetchSimulationSummaryTable,
    fetchSimulationDurationTable,
    fetchSimulationTimelineTable,
    fetchOverallChart,
    clearSimData,
    fetchCountryChart,
    fetchRegionChart,
    fetchSiteGroupChart,
    fetchCohortChartMonth,
    fetchCohortChartWeek,
    fetchCohortTimelineChart,
    fetchActualsTable,
    loadStudyActuals,
    fetchStudyRemodel,
    updateStudyRemodel,
    setStudyRemodelData,
    setIsCountryUpdate,
    fetchAllCountries,
    allCountries,
    studyActualsData,
    studyRemodelData,
    remodelLoading,
    remodelUpdateLoading,
    STUDY_PLAN_TYPES,
    canEditPlan,
    isCountryUpdate,
    createNewCohortPlan,
    seqCohorts,
    setSeqCohorts,
    seqCohortsStatus,
    validateSequentialCohort,
    setSeqCohortChangeLog,
    isSequential,
    hideEnrollmentDuration,
    seqCohortColumnHeaders,
    setSeqCohortColumnHeaders,
    updateCohortStudyPlan,
    updateStudyPlan,
    activationProfilesChanged,
    setActivationProfilesChanged,
    isEditingAPTableEnabled,
    setIsEditingAPTableEnabled,
    siteActivationStats,
  };

  return (
    <StudyContext.Provider value={value}>
      <PermissionProvider>
        {children}
      </PermissionProvider>
    </StudyContext.Provider>
  );
}
